import React, { useCallback, useMemo, useState } from 'react'
import apiClient from '../utils/api/apiClient'
import { APIEndpoints } from '../utils/api/apiConfig'
import { emptyGuid } from '../utils/emptyGuid'
import { useAssetsState } from './AssetsProvider'
import { emptyDate } from '../utils/emptyDate'
import { cloneDeep } from 'lodash';
import { setValueByKey } from '../utils/utils'
import useStores from './useStore'
import { useEffect } from 'react'


const AssetDataContext = React.createContext({})
export function useAssetDataState() {
  return React.useContext(AssetDataContext)
}

const activitiesTemplate = {
  newItems: []
}
const mainDataTemplate = {
  name: '',
  comment: '',
  number: '',
  number2: '',
  timeSpanStartDate: '',
  timeSpanEndDate: '',
  costCenter: {
    id: emptyGuid
  },
  office: {
    id: emptyGuid
  },
  group: {
    id: emptyGuid
  },
  type: {
    id: emptyGuid
  },
  responisble: {
    id: emptyGuid,
    displayName:''
  }
}

const AssetDataProvider = ({ children }) => {
  const { uiStore } = useStores()
  const [successSubmit, setSuccessSubmit] = useState(false)
  const [mainData, setMainData] = useState(cloneDeep(mainDataTemplate))
  const [initialMainData, setInitialMainData] = useState('')
  const [activities, setActivities] = useState(cloneDeep(activitiesTemplate))
  const [initialActivities, setInitialActivities] = useState('')
  const [formErrors, setFormErrors] = useState({})
  const [errorsOccurredOnSubmit, setErrorsOccurredOnSubmit] = useState(false)
  const [loadedAssetData, setLoadedAssetData] = useState(null)
  const { setForceReloadAssets } = useAssetsState()

  const setMainDataByKey = useCallback((key, value) => {
    setMainData(prev => setValueByKey(key, value, prev))
  }, [])

  const setActivitiesByKey = useCallback((key, value) => {
    setActivities(prev => setValueByKey(key, value, prev))
  }, [])

  const resetErrorByFormKey = useCallback(formKey => {
    setFormErrors(prev => {
      if (prev[formKey]) {
        delete prev[formKey]
      }
      return prev
    })
  }, [setFormErrors])

  const loadAssetData = useCallback(async id => {
    var calls = ["mainData", "activities"]
    var promises = []
    var loadedData = {}

    for (let call of calls) {
      promises.push(apiClient.getJson(APIEndpoints.assets(id)[call]))
    }
    Promise.all(promises).then((responses) => {
      calls.forEach((call, index) => loadedData[call] = responses[index])
      setLoadedAssetData(loadedData);
      initLoadedAssetData(cloneDeep(loadedData));
    })
  }, [])

  const initLoadedAssetData = (loadedData) => {
    const loadedMainData = loadedData.mainData
    const loadedActivites = loadedData.activities
    const newActivities = cloneDeep(activitiesTemplate)

    // init mainData fields
    const newMainData = {
      ...loadedMainData,
      costCenter: { id: loadedMainData.costCenter ? loadedMainData.costCenter.id : emptyGuid },
      office: { id: loadedMainData.office ? loadedMainData.office.id : emptyGuid },
      responisble: loadedMainData.responisble  ? loadedMainData.responisble : { id: emptyGuid, displayName: '' },
    };
    setMainData(newMainData)
    setInitialMainData(JSON.stringify(newMainData));

    // init jobs/ activities
    //set costcenter having null to id: emptyGuid
    loadedActivites.forEach(item => { item.costCenter = (item.costCenter === null ? { id: emptyGuid } : item.costCenter) })
    newActivities.newItems = loadedActivites
    setActivities(newActivities)
    setInitialActivities(JSON.stringify(newActivities))
  }

  const mainDataHasChanged = useMemo(() => {
    if (initialMainData !== '') {
      const mainDataString = JSON.stringify(mainData)
      return mainDataString !== initialMainData
    }
  }, [mainData, initialMainData])

  const activitiesHasChanged = useMemo(() => {
    if (initialActivities !== '') {
      const activitiesString = JSON.stringify(activities)
      return activitiesString !== initialActivities
    }
  }, [activities, initialActivities])

  const formDataHasChanged = useMemo(() => {
    return  mainDataHasChanged || activitiesHasChanged
  }, [mainDataHasChanged, activitiesHasChanged])

  const resetFormData = useCallback(() => {
    setMainData(JSON.parse(initialMainData))
    setActivities(JSON.parse(initialActivities))
    setFormErrors({})
    setErrorsOccurredOnSubmit(false)
  }, [setFormErrors, setErrorsOccurredOnSubmit, initialMainData, initialActivities])


  const parseApiFormErrors = useCallback(response => {
    if (response && response.data && response.data.errors) {
      response.data.errors.forEach(error => {
        if (error.source && error.source.pointer) {
          // const errorFormPath = error.source.pointer.replace(/^businesspartner\//, '').replace(/[\/\[]/g, '.').replace(/]/g,'')
          const errorFormPath = `${error.source.pointer}`.replace(/[[]/g, '.').replace(/]/g, '').replace(/[/]/g, '')
          setFormErrors(prev => ({ ...prev, [errorFormPath]: { msg: error.detail, apiError: error } }))
        }
      })
    }
  }, [setFormErrors])

  const getPreparedMainData = () => {
    const preparedMainData = cloneDeep(mainData);
    //prepare dates
    ["timeSpanEndDate", "timeSpanStartDate"].forEach(dateProp => {
      if (preparedMainData[dateProp] === '') {
        preparedMainData[dateProp] = emptyDate
      }
    })
    return preparedMainData
  }


  const getPreparedActivities = () => {
    const preparedActivities = cloneDeep(activities)
    preparedActivities.changedItems = preparedActivities.newItems.filter(item => loadedAssetData.activities.find(loadedActivity => loadedActivity.id === item.id))
    preparedActivities.newItems = preparedActivities.newItems.filter(item => !preparedActivities.changedItems.includes(item))
    preparedActivities.removedItems = loadedAssetData.activities.filter(item => (
      item.id !== emptyGuid && !preparedActivities.changedItems.find(foundItem => foundItem.id === item.id)
    )).map(item => item.id);
    return preparedActivities
  }

  const submitAddAsset = useCallback(async () => {
    setFormErrors({})
    setErrorsOccurredOnSubmit(false)
    var preparedData = { mainData, activities }
    try {
      const newAsset = await apiClient.postJson(APIEndpoints.assets(), preparedData)
      setSuccessSubmit(true)
      return { createdAssetId: newAsset.id, assetNo: newAsset.number }
    } catch (e) {
      setErrorsOccurredOnSubmit(true)
      if (e.response) {
        parseApiFormErrors(e.response)
      }
    }
    return null
  }, [mainData, activities, parseApiFormErrors])

  const submitEditAsset = async () => {
    setFormErrors({})
    setErrorsOccurredOnSubmit(false)
    const preparedFormData = {}

    if (mainDataHasChanged) {
      preparedFormData.mainData = getPreparedMainData()
    }
    if (activitiesHasChanged) {
      preparedFormData.activities = getPreparedActivities()
    }
    try {
      await apiClient.patchJson(APIEndpoints.assets(mainData.id).fullData, preparedFormData)
      setSuccessSubmit(true)
      await loadAssetData(mainData.id)
      setForceReloadAssets(true)
    } catch (e) {
      setErrorsOccurredOnSubmit(true)
      if (e.response) {
        parseApiFormErrors(e.response)
      }
    }
  }
  const [selectableAvtivities, setSelectableAvtivities] = useState([])
  const [loadingActivities, setLoadingActivities] = useState(false)
  const loadActivities = useCallback(async () => {
    let loadedActivities = []
    try {
      setLoadingActivities(true)
      await uiStore.loadBPDataSettings()
      if (uiStore.activitiesDependentOnSelectedOffice) {
        loadedActivities = await fetchActivities(mainData.type.id, mainData.office.id)
      }
      else {
        loadedActivities = await fetchActivities(mainData.type.id)
      }
      setSelectableAvtivities(loadedActivities)
    }
    catch (e) { }
    setLoadingActivities(false)
  }, [mainData.office.id, mainData.type.id, uiStore])

  const fetchActivities = async (selectedTypeId, selectedOfficeId) => {
    let params = {}
    params["filters.assetTypeId"] = selectedTypeId
    if (selectedOfficeId) {
      params["filters.officeId"] = selectedOfficeId
    }
    return await apiClient.getJson(APIEndpoints.allActivities, params)
  }
  useEffect(() => {
    loadActivities()
  }, [loadActivities])

  const contextValues = {
    selectableAvtivities,loadingActivities,
    loadAssetData,
    submitAddAsset,
    submitEditAsset,
    formErrors, setFormErrors,
    resetErrorByFormKey,
    errorsOccurredOnSubmit,
    formDataHasChanged,
    resetFormData,
    setMainDataByKey,
    mainData,
    setActivitiesByKey,
    activities,
    setSuccessSubmit,
    successSubmit,
    loadedAssetData,
  }
  return (
    <AssetDataContext.Provider value={contextValues}>
      {children}
    </AssetDataContext.Provider>
  )
}

export default AssetDataProvider
